/**
 * Tiny popover that appears when the user taps the 📷 in the chat composer.
 * Asks: take a new photo, pick from library, or — if they're eligible AND
 * have Snapchat connected — open the Aura AR Lens experience built on Snap
 * Camera Kit.
 *
 * Phase 29.I (Feb 2026) — Snap AR option added.
 *
 * Props
 * -----
 *  - onCamera:  open device camera
 *  - onLibrary: open photo library picker
 *  - onAR:      (optional) open Snap AR lens modal. Pass `undefined` to hide
 *               the option entirely (e.g. for users who haven't connected Snap
 *               or where AR isn't configured server-side).
 *  - arLocked:  (optional) true → AR option visible but shows lock badge +
 *               opens upsell flow when tapped.
 *  - onCancel:  close the picker.
 */
export function VisionSourcePicker({ onCamera, onLibrary, onAR, arLocked, onWallpaperPhoto, wallpaperPhotoLocked, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="vision-source-picker"
        onClick={(e) => e.stopPropagation()}
        data-testid="vision-source-picker"
      >
        <h3 className="vision-source-title">Show Aura…</h3>
        <button
          className="vision-source-option"
          onClick={onCamera}
          data-testid="vision-source-camera"
        >
          <span className="vision-source-emoji">📷</span>
          <div className="vision-source-text">
            <strong>Take a new photo</strong>
            <span>Use your camera right now</span>
          </div>
        </button>
        <button
          className="vision-source-option"
          onClick={onLibrary}
          data-testid="vision-source-library"
        >
          <span className="vision-source-emoji">🖼️</span>
          <div className="vision-source-text">
            <strong>Pick from your photos</strong>
            <span>Choose one from your library</span>
          </div>
        </button>
        {onWallpaperPhoto && (
          <button
            className={`vision-source-option vision-source-wallpaper ${wallpaperPhotoLocked ? "locked" : ""}`}
            onClick={onWallpaperPhoto}
            data-testid="vision-source-wallpaper-photo"
          >
            <span className="vision-source-emoji">🎨</span>
            <div className="vision-source-text">
              <strong>
                Paint me into a wallpaper{wallpaperPhotoLocked && <span className="vision-source-lock" aria-hidden> 🔒</span>}
              </strong>
              <span>
                {wallpaperPhotoLocked
                  ? "Premium — Aura paints you into any scene"
                  : "Aura paints you into the scene you describe"}
              </span>
            </div>
          </button>
        )}
        {onAR && (
          <button
            className={`vision-source-option vision-source-ar ${arLocked ? "locked" : ""}`}
            onClick={onAR}
            data-testid="vision-source-ar"
          >
            <span className="vision-source-emoji">✨</span>
            <div className="vision-source-text">
              <strong>
                Try Aura AR Lenses{arLocked && <span className="vision-source-lock" aria-hidden> 🔒</span>}
              </strong>
              <span>
                {arLocked
                  ? "Aurora & Eternal — peek inside"
                  : "Snap filters that match your mood"}
              </span>
            </div>
          </button>
        )}
        <button
          className="ghost-btn small"
          onClick={onCancel}
          style={{ marginTop: "0.5rem" }}
          data-testid="vision-source-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
